import React, { useEffect } from "react";
import { Box, IconButton, Typography, TextField, Grid } from "@mui/material";
import {
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import {
  cardOutline,
  fonts,
  headingFonts,
  textFieldStyles,
} from "../../../../Styles/Styles";

const ImageUpload = ({
  images,
  setImages,
  removedImages,
  setRemovedImages,
  setImageOrder, // ✅ Pass setImageOrder from parent (EditAd.js)
}) => {
  // Update imageOrder whenever images change
  useEffect(() => {
    setImageOrder(images.map((image) => image.id)); // ✅ Ensure `imageOrder` updates correctly
  }, [images, setImageOrder]);

  // Handle new image selection
  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    //  Check total count
    if (images.length + selectedFiles.length > 10) {
      alert(`You can only upload up to 10 images.`);
      return;
    }

    const newImageFiles = selectedFiles.map((file) => {
      const sizeMB = file.size / 1024 / 1024;
      return {
        id: `${Date.now()}-${Math.random()}`, // Unique ID for new images
        file,
        name: file.name,
        isNew: true, // Mark new images
        isOversized: sizeMB > 15,
      };
    });

    setImages((prevImages) => {
      const updatedImages = [...prevImages, ...newImageFiles];
      setImageOrder(updatedImages.map((img) => img.id)); // ✅ Update order on new images
      return updatedImages;
    });
  };

  // Remove image from the list
  const removeImage = (imageId) => {
    setImages((prevImages) => {
      const imageToRemove = prevImages.find((img) => img.id === imageId);
      if (imageToRemove && !imageToRemove.isNew) {
        setRemovedImages((prev) => [...prev, imageToRemove.id]); // Track removed existing images
      }

      const updatedImages = prevImages.filter((img) => img.id !== imageId);
      setImageOrder(updatedImages.map((img) => img.id)); // ✅ Update order on removal
      return updatedImages;
    });
  };

  // Move image up in the list
  const moveUp = (index) => {
    if (index === 0) return; // Already at the top
    setImages((prevImages) => {
      const newImages = [...prevImages];
      [newImages[index], newImages[index - 1]] = [
        newImages[index - 1],
        newImages[index],
      ];
      setImageOrder(newImages.map((img) => img.id)); // ✅ Update order on move
      return newImages;
    });
  };

  // Move image down in the list
  const moveDown = (index) => {
    if (index === images.length - 1) return; // Already at the bottom
    setImages((prevImages) => {
      const newImages = [...prevImages];
      [newImages[index], newImages[index + 1]] = [
        newImages[index + 1],
        newImages[index],
      ];
      setImageOrder(newImages.map((img) => img.id)); // ✅ Update order on move
      return newImages;
    });
  };

  return (
    <Box>
      <TextField
        label="Upload Images"
        type="file"
        inputProps={{ multiple: true, accept: "image/*" }}
        variant="outlined"
        required
        fullWidth
        onChange={handleImageChange}
        margin="normal"
        InputLabelProps={{ shrink: true }}
        sx={{ ...textFieldStyles }}
      />
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ ...fonts, mt: 1, mb: 1, textAlign: "left" }}
      >
        <b>No. of Images allowed: </b>10
        <br />
        <b>Max size:</b> 15MB each
      </Typography>
      {images.length > 0 ? (
        <>
          <Grid container spacing={2}>
            {images.map((image, index) => (
              <Grid item xs={12} sm={6} md={6} key={image.id}>
                <ImagePreview
                  image={image}
                  index={index}
                  removeImage={removeImage}
                  moveUp={moveUp}
                  moveDown={moveDown}
                />
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <Typography sx={{ ...fonts }}>No images selected yet.</Typography>
      )}
    </Box>
  );
};

const ImagePreview = ({ image, index, removeImage, moveUp, moveDown }) => {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        padding: 1,
        borderRadius: 2,
        border: image.isOversized ? "2px solid red" : "1px solid #ddd",
        backgroundColor: image.isOversized ? "#fff6f6" : "inherit",
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          backgroundColor: "#ff9800",
          color: "white",
          padding: "2px 6px",
          borderRadius: "0 0 4px 0",
          fontSize: "0.8rem",
          ...headingFonts,
        }}
      >
        {index + 1}
      </Box>
      <Box
        component="img"
        src={image.isNew ? URL.createObjectURL(image.file) : image.id} // Display local preview for new images
        alt={image.name}
        sx={{ width: 80, height: 80, objectFit: "cover", marginRight: 2 }}
      />
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Typography
          sx={{
            flexGrow: 1,
            fontSize: 14,
            wordBreak: "break-word", // Ensures long words wrap
            overflowWrap: "break-word", // Alternative way to wrap
            whiteSpace: "normal", // Allows text to wrap normally
          }}
        >
          {image.name}
        </Typography>
        {image.isOversized && (
          <Typography
            variant="caption"
            color="error"
            sx={{ ...headingFonts, fontWeight: "Semibold" }}
          >
            Exceeds 15MB limit
          </Typography>
        )}
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <IconButton onClick={() => moveUp(index)}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => moveDown(index)}>
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => removeImage(image.id)} sx={{ color: "red" }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ImageUpload;
